import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import { Tag } from '@carbon/react';
import { TopArgumentsCell } from './top-arguments-cell';
import { QUEUE_STATUS_TAGS, budgetReadout } from './utils';
import type {
  FailureGroupRow,
  JobClassRow,
  QueueSummaryRow,
  RunningRow,
} from './types';

const queueHelper = createColumnHelper<QueueSummaryRow>();

export const queueColumns = [
  queueHelper.accessor('name', {
    id: 'name',
    header: 'Queue',
    size: 240,
    meta: { type: 'text' },
  }),
  queueHelper.accessor('depth', {
    id: 'depth',
    header: 'Depth',
    size: 120,
    meta: { type: 'integer' },
  }),
  queueHelper.accessor('latencySeconds', {
    id: 'latencySeconds',
    header: 'Latency',
    size: 120,
    meta: { type: 'duration' },
  }),
  queueHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    size: 130,
    meta: { type: 'text' },
    cell: (info) => {
      const { label, type } = QUEUE_STATUS_TAGS[info.getValue()];
      return (
        <Tag type={type} size="sm">
          {label}
        </Tag>
      );
    },
  }),
  queueHelper.accessor('budgetSeconds', {
    id: 'budgetSeconds',
    header: 'SLA Budget',
    size: 200,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (info) =>
      budgetReadout(info.row.original.latencySeconds, info.getValue()),
  }),
  queueHelper.accessor('paused', {
    id: 'paused',
    header: 'Paused',
    size: 120,
    meta: { type: 'boolean' },
    cell: (info) =>
      info.getValue() ? (
        <Tag type="red" size="sm">
          Paused
        </Tag>
      ) : (
        'No'
      ),
  }),
] as ColumnDef<QueueSummaryRow>[];

const runningHelper = createColumnHelper<RunningRow>();

export const runningColumns = [
  runningHelper.accessor('queue', {
    id: 'queue',
    header: 'Queue',
    size: 200,
    meta: { type: 'text' },
  }),
  runningHelper.accessor('jobClass', {
    id: 'jobClass',
    header: 'Job Class',
    size: 360,
    meta: { type: 'text' },
  }),
  runningHelper.accessor('count', {
    id: 'count',
    header: 'Running',
    size: 110,
    meta: { type: 'integer' },
  }),
  runningHelper.accessor('longestRunningSeconds', {
    id: 'longestRunningSeconds',
    header: 'Longest Running',
    size: 150,
    meta: { type: 'duration' },
  }),
  runningHelper.accessor('oldestStartedAt', {
    id: 'oldestStartedAt',
    header: 'Oldest Started',
    size: 200,
    meta: { type: 'datetime' },
  }),
] as ColumnDef<RunningRow>[];

/**
 * A column header, marked as describing the sample when the scan was sampled.
 *
 * @param {string} header - The plain header.
 * @param {boolean} sampled - Whether the scan stopped before reading everything.
 * @return {string} The header to show.
 */
const inSample = (header: string, sampled: boolean) =>
  sampled ? `${header} in Sample` : header;

const jobClassHelper = createColumnHelper<JobClassRow>();

/**
 * The job-class scan columns. A sampled scan read only the newest jobs, so its
 * count, share and oldest/newest columns describe the sample, not the queue,
 * and say so.
 *
 * @param {boolean} sampled - Whether the scan stopped before reading every job.
 * @return The column definitions.
 */
const buildJobClassColumns = (sampled: boolean) =>
  [
    jobClassHelper.accessor('jobClass', {
      id: 'jobClass',
      header: 'Job Class',
      size: 320,
      meta: { type: 'text' },
    }),
    jobClassHelper.accessor('count', {
      id: 'count',
      header: inSample('Count', sampled),
      size: 130,
      meta: { type: 'integer' },
    }),
    jobClassHelper.accessor('percentage', {
      id: 'percentage',
      header: inSample('%', sampled),
      size: 100,
      meta: {
        type: 'percent',
        format: { minDecimalPlaces: 1, maxDecimalPlaces: 1 },
      },
    }),
    jobClassHelper.accessor('oldestEnqueuedAt', {
      id: 'oldestEnqueuedAt',
      header: inSample('Oldest Enqueued', sampled),
      size: 200,
      meta: { type: 'datetime' },
    }),
    jobClassHelper.accessor('newestEnqueuedAt', {
      id: 'newestEnqueuedAt',
      header: inSample('Newest Enqueued', sampled),
      size: 200,
      meta: { type: 'datetime' },
    }),
    jobClassHelper.accessor('topArguments', {
      id: 'topArguments',
      header: 'Most Common Arguments',
      size: 520,
      enableSorting: false,
      enableColumnFilter: false,
      cell: (info) => <TopArgumentsCell topArguments={info.getValue()} />,
    }),
  ] as ColumnDef<JobClassRow>[];

export const jobClassColumns = buildJobClassColumns(false);
export const sampledJobClassColumns = buildJobClassColumns(true);

const failureHelper = createColumnHelper<FailureGroupRow>();

/**
 * The retry/dead breakdown columns. A sampled scan read only part of the set,
 * so its count and last-failed columns describe the sample, and say so.
 *
 * @param {boolean} sampled - Whether the scan stopped before reading every entry.
 * @return The column definitions.
 */
const buildFailureColumns = (sampled: boolean) =>
  [
    failureHelper.accessor('queue', {
      id: 'queue',
      header: 'Queue',
      size: 180,
      meta: { type: 'text' },
    }),
    failureHelper.accessor('jobClass', {
      id: 'jobClass',
      header: 'Job Class',
      size: 300,
      meta: { type: 'text' },
    }),
    failureHelper.accessor('errorClass', {
      id: 'errorClass',
      header: 'Error Class',
      size: 220,
      meta: { type: 'text' },
    }),
    failureHelper.accessor('errorMessage', {
      id: 'errorMessage',
      header: 'Error Message',
      size: 420,
      meta: { type: 'text' },
    }),
    failureHelper.accessor('count', {
      id: 'count',
      header: inSample('Count', sampled),
      size: 130,
      meta: { type: 'integer' },
    }),
    failureHelper.accessor('lastFailedAt', {
      id: 'lastFailedAt',
      header: inSample('Last Failed', sampled),
      size: 200,
      meta: { type: 'datetime' },
    }),
  ] as ColumnDef<FailureGroupRow>[];

export const failureColumns = buildFailureColumns(false);
export const sampledFailureColumns = buildFailureColumns(true);
